/**
 * The verifications inbox: what has been asked of this reader — or of the
 * firm they are standing in — and what they have already answered.
 *
 * Three kinds of card share the screen because they share one shape,
 * somebody waiting on the reader: a verification request, an affiliation
 * claim, and an agent asking to represent a work. This file turns each into a
 * card with every label decided, filters them, counts what is owed, and words
 * a refusal; the words themselves are `verifications.constants.ts`'s and the
 * reads and writes `useVerificationsController.ts`'s.
 *
 * Two answers are the server's and never this file's. `canAnswer` on a claim
 * or a representation card comes off the same set the write admits, and
 * `capacitiesFor` offers only the organisations the write would accept — a
 * control this screen invents is a button that produces a 403.
 */

import { isResolved, wasAnswered } from "@ondo/shared";

import { formatDay } from "../lib/dates.ts";
import { HISTORY_KINDS } from "../provenance/provenance.constants.ts";
import type { CaseCard, LogLine } from "./attributionService.model.ts";
import type {
  ProvenanceEntryKind,
  AffiliationClaim,
  MeResponse,
  ProvenanceDocument,
  RepresentationRequestReceived,
  VerificationAnswer,
  VerificationAspect,
  VerificationRequestSummary,
} from "@ondo/shared";

import {
  AFFILIATION_KIND_LABELS,
  AFFILIATION_OUTCOME_LABELS,
  REPRESENTATION_OUTCOME_LABELS,
  ASPECT_BLURBS,
  ASPECT_LABELS,
  ANSWER_LABELS,
  EMPTY_COPY,
  MAX_BULK,
  METHOD_LABELS,
  OUTCOME_LABELS,
  RESOLUTION_COPY,
  RESOLUTION_LEADS,
  STANCE_LABELS,
  VERIFICATION_COPY,
} from "./verifications.constants.ts";
import type {
  AspectFilter,
  StatusFilter,
  VerificationTab,
} from "./verifications.constants.ts";

/**
 * The four answers, and never `refer` beside them.
 *
 * Referring does not end the question — it opens the same question for somebody
 * else — so it is a different kind of act and the model keeps it out of this
 * list rather than filtering it back out at each use.
 */
export const ANSWERS: readonly VerificationAnswer[] = [
  "confirm",
  "dispute",
  "no_recall",
  "decline",
];

export interface Capacity {
  /** 'ONDO-4001', or null for the caller themselves. */
  ondoId: string | null;
  label: string;
}

export interface DocumentRow {
  id: string;
  name: string;
  meta: string;
  /** `null` is not `false` — see `ProvenanceDocument.awaiting`. */
  status: { label: string; tone: "confirmed" | "awaiting" | "unknown" } | null;
  /**
   * What the row says the bytes are, carried through to the viewer.
   *
   * Never a filename extension — the same rule `SpotlightSource` states, and it
   * matters most here: a verifier deciding whether to put their name to a sale
   * agreement must be shown the agreement, not an `<img>` pointed at a PDF.
   */
  contentType: string;
  /** Where the bytes live in the `provenance` bucket. Signed on demand. */
  objectPath: string;
  /** 'Open Private sale agreement' — the accessible name of the control. */
  openLabel: string;
}

/**
 * What was answered, on a card that has been.
 *
 * Not a chip. Both confirming and disputing close a request as `attested`, so a
 * card that showed only the outcome told the reader they had replied and not
 * which way — the one thing they came back to check. This carries the act, the
 * sentence that says what it did to the record, and the words left with it.
 */
export interface Resolution {
  /** 'Confirmed' / 'Disputed' / 'Passed on'. The stamp in the card head. */
  label: string;
  tone: "confirmed" | "disputed" | "neutral";
  lead: string;
  /** The words left with the answer, under the label that fits the act. */
  reason: { label: string; body: string } | null;
  /**
   * Said when an answer carried no words, and null where there was no answer to
   * carry any. "Nothing was added" is a fact about a reply; on a withdrawn
   * request it would be a remark about a reply that never happened.
   */
  silent: string | null;
}

export interface RequestCard {
  requestId: string;
  entryId: string;
  /**
   * Which kind of history the request is about — OND-212.
   *
   * The Authentication Requests screen pulls the authorship claims out of the
   * general inbox by this and nothing else. It is a fact the summary already
   * carries, surfaced rather than re-derived from the title.
   */
  kind: ProvenanceEntryKind;
  aspect: VerificationAspect;
  title: string;
  blurb: string;
  awaiting: boolean;
  /** What was recorded, or null while the request is still open. */
  resolution: Resolution | null;
  askedOn: string;
  /** '2 Mar 2026' — `askedOn` as the card prints it. */
  askedOnLabel: string;

  requestedBy: { name: string; ondoId: string } | null;
  /** Only when the request went to an organisation rather than to the person. */
  askedOf: { name: string; ondoId: string } | null;
  /** What the asker said they had in mind. Their words; asserts nothing. */
  capacityHint: string | null;

  work: { title: string; byline: string; imagePath: string | null };
  what: string;
  dates: { value: string; label: string }[];
  documents: DocumentRow[];

  /**
   * Whether this seat may answer, and why not when it may not.
   *
   * False for a member below `editor` at the organisation asked. They read the
   * request so they can prepare the answer and cannot commit the organisation's
   * name to it.
   */
  canAnswer: boolean;
  refusal: string | null;
}

export interface ClaimCard {
  claimId: string;
  title: string;
  claimedBy: { name: string; ondoId: string } | null;
  subject: { name: string; ondoId: string };
  period: string | null;
  note: string | null;
  awaiting: boolean;
  outcome: string | null;
  canAnswer: boolean;
}

/**
 * What the screen was opened ON, when it was reached from a link.
 *
 * A notification's action button, today; an emailed link, plausibly. The screen
 * still shows the whole inbox — this only says which row to take the reader to.
 */
export interface Focus {
  kind: "request" | "claim" | "representation";
  id: string;
}

/**
 * An agent asking to represent one of the reader's works.
 *
 * A card rather than a `ClaimCard` with different words, because the two answer
 * different questions. A claim asks "is this true"; this asks "may they act for
 * you", and the reader needs to see the WORK — the title, the artist, the
 * photograph — to answer it at all. `ClaimCard` has no subject that is a record.
 */
export interface RepresentationCard {
  requestId: string;
  workId: string;
  title: string;
  attributedName: string;
  year: string | null;
  imagePath: string | null;
  /** The agent, named — they put their own name to the request. */
  agentName: string;
  agentOndoId: string;
  agentVerified: boolean;
  /** Why they are asking, in their words. Null when they said nothing. */
  note: string | null;
  awaiting: boolean;
  /** "Granted", "Declined", "Withdrawn" — empty while it is open. */
  outcome: string;
  responseNote: string | null;
  /** The database's answer, never this file's. */
  canAnswer: boolean;
}

export interface VerificationsOpen {
  kind: "open";
  tab: VerificationTab;
  counts: {
    requests: number;
    affiliations: number;
    representation: number;
    /**
     * OND-60's cases — ALL of them, on both sides.
     *
     * The tab strip prints a total rather than a pending count, matching the
     * other three. What is waiting on the reader is in `awaiting` below, and
     * `pendingCount` in `attributionService.model.ts` is deliberately not
     * folded into it: a collector's four open cases are not four things they
     * can do, and a banner that counted them would never go down.
     */
    service: number;
    /**
     * How many rows are waiting on the reader, ACROSS ALL THREE TABS.
     *
     * The banner reads "N awaiting your response", and it counts every tab
     * because the reader's question is how much is owed by them rather than how
     * much is owed in the tab they happen to have open. A count that changed as
     * they clicked between tabs would be answering a question nobody asked.
     */
    awaiting: number;
  };
  status: StatusFilter;
  aspect: AspectFilter;
  rows: RequestCard[];
  claims: ClaimCard[];
  representation: RepresentationCard[];
  /** OND-60's cases, newest first, from whichever side the reader is on. */
  cases: CaseCard[];
  /** The log for the one case a reader has opened, keyed by request. */
  caseLog: Record<string, LogLine[]>;
  /** Null when there is something to show. Not a boolean — the copy differs. */
  empty: { lead: string; detail: string } | null;
  selected: string[];
  capacities: Capacity[];
  busy: boolean;
  error: string | null;
  /**
   * The row a link asked for, and whether it is here.
   *
   * `id` is null when the screen was opened plainly. `missing` is the case a
   * link has to handle and a form never does: a request answered somewhere else,
   * withdrawn, or no longer visible to this account. The screen says so rather
   * than rendering an ordinary inbox — a reader who clicked "Review" and landed
   * on an unremarkable list would reasonably conclude the link was broken.
   */
  focus: { id: string; missing: boolean } | null;
}

export type VerificationsView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | VerificationsOpen;

/**
 * Organisations the caller may answer in the name of.
 *
 * THE `history_confirm` PRIVILEGE SINCE OND-61, where this was the `editor`
 * rung. It still mirrors `ondo.answering_capacity_principal_id()` exactly —
 * which is the whole requirement, because a capacity the server would refuse
 * must not be offered or the control is a button that produces a 403 — and that
 * function now reads `ondo.principal_ids_with('history_confirm')`.
 *
 * READING THE RUNG WOULD NOW BE WRONG IN BOTH DIRECTIONS, which is why this
 * could not be left alone:
 *
 * * It would offer the capacity to an editor-rung seat whose bundle withholds
 *   it, and the server would refuse the answer after the reader had typed it.
 * * It would WITHHOLD it from the ticket's own Authentication Specialist, who
 *   sits at the viewer rung and whose bundle grants it. `can_attest` on his row
 *   is true, so the screen would show him the control and then offer him no
 *   capacity to exercise it — the exact "refused every time he tries" OND-61
 *   exists to end, moved from the database into the client.
 *
 * Returns the empty list when there is nothing to choose, and the view renders
 * no picker at all rather than one with a single option. Answering as yourself
 * is the ordinary case and does not need a control to say so.
 */
export function capacitiesFor(me: MeResponse | null): Capacity[] {
  if (!me) return [];

  const orgs = me.memberships
    .filter((m) => m.status === "active" && m.privileges.includes("history_confirm"))
    .map((m) => ({
      ondoId: m.organisation.ondoId,
      label: m.organisation.displayName,
    }));

  if (orgs.length === 0) return [];

  return [{ ondoId: null, label: VERIFICATION_COPY.onBehalfSelf }, ...orgs];
}

/**
 * The title of a card.
 *
 * An acquisition request reads under the entry's own `method` — "Gift
 * verification", "Bequest verification" — because that is the fact being
 * confirmed and the registry already records the word for it. An `entry`
 * request reads under the entry's KIND for the same reason: "Exhibition
 * verification" says what is being asked, and "Entry verification" is a phrase
 * nobody in this trade uses.
 *
 * A `method` of null falls back to the aspect's own label rather than to a
 * word: only `sale` carries one, and an acquisition request on a kind that has
 * no method is a state the API does not produce.
 */
export function cardTitle(request: VerificationRequestSummary): string {
  if (request.aspect === "entry") {
    return `${HISTORY_KINDS[request.kind].chip} verification`;
  }

  if (request.aspect !== "acquisition" || request.method === null) {
    return ASPECT_LABELS[request.aspect];
  }

  return `${METHOD_LABELS[request.method]} verification`;
}

/** 'Erwin Wurm · 2001 · Painted polyester'. Skips what the server withheld. */
export function byline(request: VerificationRequestSummary): string {
  return [request.workAttributedName, request.workYear, request.workMedium]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/**
 * The dates the request is about.
 *
 * Free text straight through, because that is what the registry holds: '1962',
 * 'c. 1938' and 'by 1971' are all real, and a client that parsed them into dates
 * would have to invent a day for each.
 */
export function dateChips(
  request: VerificationRequestSummary,
): { value: string; label: string }[] {
  const chips: { value: string; label: string }[] = [];

  if (request.startText) chips.push({ value: request.startText, label: "From" });
  if (request.endText) chips.push({ value: request.endText, label: "Until" });
  // No end is "to the present" and there is no boolean beside it to disagree.
  else if (request.startText) chips.push({ value: "the present", label: "Until" });

  return chips;
}

/**
 * A document's state, three-valued.
 *
 * `awaiting: null` means the reader is entitled to neither answer — that a
 * request exists is restricted to the asker and the asked — so it renders as
 * "not yet confirmed" rather than as "awaiting". Collapsing null to false would
 * report "nothing is pending" to somebody who was told nothing.
 */
export function toDocumentRow(document: ProvenanceDocument): DocumentRow {
  const kb = Math.max(1, Math.round(document.byteSize / 1024));
  const meta = [
    document.contentType === "application/pdf" ? "PDF" : "Image",
    `${kb} KB`,
    document.uploadedBy ? `added by ${document.uploadedBy.displayName}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

  const name = document.label ?? "Untitled document";

  return {
    id: document.id,
    name,
    meta,
    contentType: document.contentType,
    objectPath: document.objectPath,
    openLabel: `${VERIFICATION_COPY.openDocument} ${name}`,
    status: document.confirmedAt
      ? { label: VERIFICATION_COPY.documentConfirmed, tone: "confirmed" }
      : document.awaiting === true
        ? { label: VERIFICATION_COPY.documentAwaiting, tone: "awaiting" }
        : { label: VERIFICATION_COPY.documentNeither, tone: "unknown" },
  };
}

/**
 * Which documents a click opens, and which of them it opens on.
 *
 * The whole card's evidence rather than the one clicked, so paging inside the
 * viewer works — and it costs nothing, because signing a set is one round trip
 * whichever way. The same shape `provenance.model.ts` uses, for the same
 * reason: a verifier reading one invoice is usually reading all of them.
 *
 * Null for a click on a card or a document the inbox no longer holds, which is
 * what a stale click after a re-read looks like.
 */
export function documentsToOpen(
  rows: RequestCard[],
  requestId: string,
  documentId: string,
): { documents: DocumentRow[]; index: number } | null {
  const card = rows.find((row) => row.requestId === requestId);
  if (!card) return null;

  const index = card.documents.findIndex((document) => document.id === documentId);

  return index === -1 ? null : { documents: card.documents, index };
}

/**
 * What a resolved request recorded, or null while it is still open.
 *
 * Keyed on the STANCE first and the outcome second, because `attested` is both
 * answers: `ondo.answer_verification_request()` maps confirm and dispute alike
 * to it and writes which one to `response_stance`. Reading the outcome alone is
 * how a rejection came to render as "Answered".
 *
 * The stance is nullable in the type and the fallback is not defensive
 * decoration — a deployment older than 20260808090000 answered without setting
 * it, and those rows still say "Answered" rather than guessing at a position
 * nobody recorded.
 */
export function resolutionFor(request: VerificationRequestSummary): Resolution | null {
  // `isResolved` inverted, written as the null test because that is the one
  // TypeScript narrows on — everything below indexes a record by this outcome.
  if (request.outcome === null) return null;

  const stance = request.outcome === "attested" ? request.responseStance : null;
  const body = request.responseBody?.trim() ?? "";

  return {
    label: stance ? STANCE_LABELS[stance] : OUTCOME_LABELS[request.outcome],
    tone: stance === "verify" ? "confirmed" : stance === "dispute" ? "disputed" : "neutral",
    lead: RESOLUTION_LEADS[stance ?? request.outcome],
    reason: body
      ? {
          label:
            stance === "dispute" ? RESOLUTION_COPY.reasonLabel : RESOLUTION_COPY.noteLabel,
          body,
        }
      : null,
    // Only where somebody at this end answered. See `Resolution.silent`.
    silent: !body && wasAnswered(request.outcome) ? RESOLUTION_COPY.silent : null,
  };
}

export function toCard(
  request: VerificationRequestSummary,
  documents: ProvenanceDocument[] | undefined,
): RequestCard {
  const awaiting = !isResolved(request.outcome);

  return {
    requestId: request.requestId,
    entryId: request.entryId,
    kind: request.kind,
    aspect: request.aspect,
    title: cardTitle(request),
    blurb: ASPECT_BLURBS[request.aspect],
    awaiting,
    resolution: resolutionFor(request),
    askedOn: request.createdAt,
    askedOnLabel: formatDay(request.createdAt, { day: "numeric" }),
    // Null renders as "Withheld" rather than as a blank. The server types this
    // nullable and a card with an empty box where a name goes reads as a bug.
    requestedBy: request.askedBy
      ? { name: request.askedBy.displayName, ondoId: request.askedBy.ondoId }
      : null,
    // Only when it went to an organisation. Telling somebody a request
    // addressed to them personally was addressed to them personally is noise.
    askedOf: request.askedOfIsOrg
      ? { name: request.askedOf.displayName, ondoId: request.askedOf.ondoId }
      : null,
    capacityHint: request.requestedCapacity,
    work: {
      title: request.workTitle ?? "Untitled",
      byline: byline(request),
      imagePath: request.workImagePath,
    },
    // The asker's own words when they gave any, and the aspect's sentence when
    // they did not. Never a sentence assembled out of the entry's contents —
    // those are claims about third parties, and restating them as though the
    // registry said them is the thing `ProvenanceActor` guards against.
    what: request.note ?? ASPECT_BLURBS[request.aspect],
    dates: dateChips(request),
    documents: (documents ?? []).map(toDocumentRow),
    canAnswer: awaiting && request.canAttest,
    refusal:
      awaiting && !request.canAttest
        ? "Reading this is yours; answering in the organisation’s name is not."
        : null,
  };
}

/** Matches a card against the two filter rows. Exported for its own test. */
export function matches(
  card: RequestCard,
  status: StatusFilter,
  aspect: AspectFilter,
): boolean {
  if (status === "awaiting" && !card.awaiting) return false;
  if (status === "resolved" && card.awaiting) return false;

  return aspect === "all" || card.aspect === aspect;
}

/** The cards the two filter rows leave showing, in the order they arrived. */
export function visibleCards(
  cards: readonly RequestCard[],
  status: StatusFilter,
  aspect: AspectFilter,
): RequestCard[] {
  return cards.filter((card) => matches(card, status, aspect));
}

/**
 * How many of these still wait on the reader.
 *
 * Structural, so the three tabs' cards are counted by one function and summed:
 * the banner asks how much is owed by the reader, not by the open tab.
 */
export function awaitingCount(rows: readonly { awaiting: boolean }[]): number {
  return rows.filter((row) => row.awaiting).length;
}

export function toClaimCard(claim: AffiliationClaim): ClaimCard {
  const awaiting = claim.outcome === null;
  const period = [claim.startText, claim.endText].filter(Boolean).join(" – ");

  return {
    claimId: claim.claimId,
    title: [AFFILIATION_KIND_LABELS[claim.kind], claim.title]
      .filter((part): part is string => Boolean(part))
      .join(" · "),
    claimedBy: claim.claimedBy
      ? { name: claim.claimedBy.displayName, ondoId: claim.claimedBy.ondoId }
      : null,
    subject: { name: claim.subject.displayName, ondoId: claim.subject.ondoId },
    period: period || null,
    note: claim.note,
    awaiting,
    outcome: claim.outcome ? AFFILIATION_OUTCOME_LABELS[claim.outcome] : null,
    canAnswer: awaiting && claim.canAnswer,
  };
}

/**
 * One representation request, as the owner reads it.
 *
 * `canAnswer` is the server's, not this file's — `ondo.representation_requests_for_me()`
 * computes it from the same set the answer admits, so the screen cannot offer a
 * control the database would refuse. The same rule `ClaimCard.canAnswer` and
 * `WorkAgent.canEnd` keep.
 */
export function toRepresentationCard(
  request: RepresentationRequestReceived,
): RepresentationCard {
  return {
    requestId: request.requestId,
    workId: request.workId,
    title: request.title,
    attributedName: request.attributedName,
    year: request.year,
    imagePath: request.imagePath,
    agentName: request.agent.displayName,
    agentOndoId: request.agent.ondoId,
    agentVerified: request.agent.verified,
    note: request.note,
    awaiting: request.outcome === null,
    // Empty string rather than null while it is open, so the stamp renders the
    // awaiting badge — the shape `ClaimCard` uses one field over.
    outcome: request.outcome ? REPRESENTATION_OUTCOME_LABELS[request.outcome] : "",
    responseNote: request.responseNote,
    canAnswer: request.canAnswer,
  };
}

/**
 * Which empty state, or none.
 *
 * Three distinct ones, because they mean different things to the reader and one
 * generic "nothing here" would lie about two of them. "Nothing has ever been
 * asked of you" is a fact about the account; "you have answered everything" is
 * praise; "your filters match nothing" is a hint to change them.
 */
export function emptyFor(input: {
  total: number;
  shown: number;
  awaiting: number;
}): { lead: string; detail: string } | null {
  if (input.shown > 0) return null;
  if (input.total === 0) return EMPTY_COPY.never;
  if (input.awaiting === 0) return EMPTY_COPY.awaiting;

  return EMPTY_COPY.filtered;
}

/**
 * Whether a bulk action can be sent, and what stops it.
 *
 * A dispute needs a reason wherever it is recorded — the same rule the check
 * constraint enforces — and in bulk that is ONE reason applied to every
 * selection, which the confirm sheet shows before anything is sent.
 */
export function bulkRefusal(input: {
  answer: VerificationAnswer;
  selected: string[];
  reason: string;
}): string | null {
  if (input.selected.length === 0) return "Choose at least one to answer.";
  if (input.selected.length > MAX_BULK) {
    return `That is more than ${MAX_BULK} at once. Answering is putting your name to each of them.`;
  }
  if (input.answer === "dispute" && input.reason.trim().length === 0) {
    return "Say why you dispute these.";
  }

  return null;
}

/** The sentence the confirm sheet leads with. Named for the act, not the count. */
/**
 * What a part-refused bulk answer says, naming the requests that did not land.
 *
 * `answerMany` used to await inside `run`, so the FIRST refusal aborted the
 * loop and produced one sentence naming no request at all — a reader who
 * selected twenty and had the fourth refused was told "This was already
 * answered somewhere else" with no way to know which, and the sixteen after it
 * were never attempted. The comment above that loop said sequential existed so
 * that "one failure names one request"; this is that sentence.
 *
 * Every row is attempted. A refusal on one is a fact about that row — somebody
 * answered it from another seat, or withdrew it — and says nothing about the
 * nineteen behind it.
 */
export function bulkRefusalReport(
  failures: readonly { title: string; message: string }[],
  total: number,
): string {
  const [first] = failures;
  if (!first) return "";

  const landed = total - failures.length;
  const settled =
    landed === 0
      ? "None of them went through."
      : `${String(landed)} of ${String(total)} went through.`;

  if (failures.length === 1) {
    return `${settled} ${first.title} was refused: ${first.message}`;
  }

  // Named rather than counted. A tally tells the reader how much went wrong and
  // not which row to look at, and the row is the part they can act on.
  const named = failures.map((failure) => failure.title).join(", ");

  return `${settled} These were refused: ${named}.`;
}

export function bulkSummary(answer: VerificationAnswer, count: number): string {
  const verb = ANSWER_LABELS[answer].replace(/^[^\p{L}]+/u, "");

  return `${verb} — ${count} ${count === 1 ? "request" : "requests"}`;
}

/**
 * Turns a failed write into a sentence.
 *
 * The three the server can raise on an answer, each mapped to the thing the
 * reader can do about it. A 403 on an open request means somebody else closed it
 * — the asker withdrew it, or another editor answered — and saying "forbidden"
 * would describe the mechanism rather than what happened.
 */
export function refusalFor(status: number, message: string): string {
  if (status === 409) return VERIFICATION_COPY.alreadyAnswered;
  if (status === 403 && /already hold a position/.test(message)) {
    return VERIFICATION_COPY.positionHeld;
  }
  if (status === 403) return VERIFICATION_COPY.withdrawn;

  return message;
}
